import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Search, X, Filter, Mail, Phone, CreditCard } from 'lucide-react';

const SubscriptionSearch = ({ searchTerm, onSearchChange, searchType, onSearchTypeChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const searchTypes = [
    { key: 'all', label: 'All', icon: Search },
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'phone', label: 'Phone', icon: Phone },
    { key: 'plan', label: 'Plan', icon: CreditCard }
  ];

  const handleSearch = (value) => {
    onSearchChange(value);
  };

  const clearSearch = () => {
    onSearchChange('');
    onSearchTypeChange('all');
  };

  const getPlaceholder = () => {
    switch (searchType) {
      case 'email':
        return 'Search by email address...';
      case 'phone':
        return 'Search by phone number...';
      case 'plan':
        return 'Search by plan name...';
      default:
        return 'Search subscriptions by email, phone, or plan...';
    }
  };

  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-200 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500"
      >
        <div className="flex items-center p-4">
          {/* Search Type Selector */}
          <div className="relative mr-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex gap-2 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg border border-gray-200 transition-colors hover:bg-gray-100"
            >
              {React.createElement(searchTypes.find(t => t.key === searchType)?.icon || Search, { 
                className: "w-4 h-4" 
              })}
              <span className="hidden sm:inline">
                {searchTypes.find(t => t.key === searchType)?.label || 'All'}
              </span>
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <Filter className="w-3 h-3" />
              </motion.div>
            </motion.button>

            {/* Dropdown */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="absolute z-50 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-lg"
                >
                  {searchTypes.map((type) => (
                    <button
                      key={type.key}
                      onClick={() => {
                        onSearchTypeChange(type.key);
                        setIsExpanded(false);
                      }}
                      className={`flex items-center gap-3 w-full px-4 py-3 text-left text-sm hover:bg-gray-50 transition-colors ${
                        searchType === type.key ? 'bg-purple-50 text-purple-700' : 'text-gray-700'
                      }`}
                    >
                      <type.icon className="w-4 h-4" />
                      {type.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={getPlaceholder()}
              className="py-3 pr-12 pl-10 w-full placeholder-gray-500 text-gray-900 bg-transparent border-none outline-none"
            />
          </div>

          {/* Clear Button */}
          {searchTerm && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={clearSearch}
              className="p-2 text-gray-400 transition-colors hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </motion.button>
          )}
        </div>

        {/* Search Suggestions */}
        <AnimatePresence>
          {searchTerm && searchType === 'all' && (
            <motion.div
              initial={{ opacity: 0, height: 0, y: -10 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="px-4 py-3 bg-gray-50 border-t border-gray-100"
            >
              <div className="flex flex-wrap gap-2">
                <span className="text-xs font-medium text-gray-500">Quick filters:</span>
                {searchTypes.slice(1).map((type) => (
                  <motion.button
                    key={type.key}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onSearchTypeChange(type.key)}
                    className="flex gap-1 items-center px-2 py-1 text-xs font-medium text-gray-600 bg-white rounded-md border border-gray-200 transition-colors hover:bg-gray-50 hover:border-gray-300"
                  >
                    <type.icon className="w-3 h-3" />
                    {type.label}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Click outside to close dropdown */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsExpanded(false)}
        />
      )}
    </div>
  );
};

export default SubscriptionSearch; 